import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from "@tanstack/react-query";
import { useOrganizationStore } from "@/zapi-frontend/stores/organization";
import $api from "../api/axios";
import type { TSavedFilter, TSavedFilterValues } from "../types/savedFilters";

export const GET_SAVED_FILTERS_KEY = "getSavedFilters";

type TQueryOpts = Partial<UseQueryOptions<TSavedFilter[], Error>>;

export function useSavedFilters(options?: TQueryOpts) {
  const { currentOrganizationId } = useOrganizationStore();

  return useQuery({
    queryKey: [GET_SAVED_FILTERS_KEY, currentOrganizationId],
    queryFn: async () => {
      const { data } = await $api.get<{ data: TSavedFilter[] }>(
        `/organizations/${currentOrganizationId}/saved-filters`
      );
      return data.data;
    },
    enabled: currentOrganizationId > 0,
    ...options
  });
}

export function useCreateSavedFilter() {
  const { currentOrganizationId } = useOrganizationStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { name: string; filters: TSavedFilterValues }) => {
      const { data } = await $api.post<{ data: TSavedFilter }>(
        `/organizations/${currentOrganizationId}/saved-filters`,
        input
      );
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [GET_SAVED_FILTERS_KEY, currentOrganizationId] });
    }
  });
}

export function useUpdateSavedFilter() {
  const { currentOrganizationId } = useOrganizationStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { id: number; name?: string; filters?: TSavedFilterValues }) => {
      const { id, ...patch } = input;
      const { data } = await $api.patch<{ data: TSavedFilter }>(
        `/organizations/${currentOrganizationId}/saved-filters/${id}`,
        patch
      );
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [GET_SAVED_FILTERS_KEY, currentOrganizationId] });
    }
  });
}

export function useDeleteSavedFilter() {
  const { currentOrganizationId } = useOrganizationStore();
  const queryClient = useQueryClient();
  const queryKey = [GET_SAVED_FILTERS_KEY, currentOrganizationId];

  return useMutation({
    mutationFn: async (id: number) => {
      await $api.delete(`/organizations/${currentOrganizationId}/saved-filters/${id}`);
      return id;
    },
    onMutate: async (id: number) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<TSavedFilter[]>(queryKey);
      queryClient.setQueryData<TSavedFilter[]>(queryKey, (current) =>
        (current ?? []).filter((filter) => filter.id !== id)
      );
      return { previous };
    },
    onError: (_err, _id, context) => {
      if (context?.previous) queryClient.setQueryData(queryKey, context.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    }
  });
}
